import {
    AfterViewInit,
    Component,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
    ViewContainerRef,
} from '@angular/core';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { Actions, ofType } from "@ngrx/effects";
import { Store, select } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { selectSeButtonsTimeline, selectSeButtonsToolbarRightSlider } from '../../root-store/root.selectors';
import { RootState } from '../../root-store/root.state';
import { seToolbarToggleRightSlider, seToolbarToggleTimeline } from '../../se-toolbar/se-buttons-toolbar/redux/se-buttons-toolbar.actions';
import { ToolbarSliderOptions } from '../../se-toolbar/se-buttons-toolbar/types';
import { seWorklistType } from "../../search-page/redux/search-page.actions";
import { SearchFormType } from "../../search-page/search-page-type.enum";
import { sideTabClicked } from '../redux/se-side-nav.actions';
import { selectActiveSeSideComponentState } from '../redux/se-side-nav.selectors';
import { SeSideComponentType } from '../se-side-components/se-side-components-models';
import { ReportingComponentProps, SeSideService } from '../se-side.service';
import { selectSeReportExaminationStudies } from '../../report/report.selectors';

@Component({
    selector: 'se-side-nav',
    templateUrl: './se-side-nav.component.html',
    styleUrls: ['./se-side-nav.component.scss'],
    standalone: false
})
export class SeSideNavComponent implements AfterViewInit, OnInit, OnDestroy {
    @ViewChildren('reportingContainer', { read: ViewContainerRef }) reportingContainer?: QueryList<ViewContainerRef>;
    @ViewChild('matTabGroup') matTabs: MatTabGroup | undefined;

    activeSideComponents$ = this.store.pipe(select(selectActiveSeSideComponentState));
    activeSideComponentsSnapshot: any[] = [];
    examinationStudies$ = this.store.pipe(select(selectSeReportExaminationStudies));
    closed: boolean = false;
    formType!: SearchFormType;
    hideContent: boolean = false;
    isTimelineActiveOnWl: boolean = false;
    reportingComponentSubscription?: Subscription;
    rightSlider$: Observable<ToolbarSliderOptions>;
    selectedTabIndex: number = 0;
    show: boolean = true;
    sideComponentType = SeSideComponentType;
    subscriptions: Subscription[] = [];
    private actionSubscription!: Subscription;
    private timelineStateSubscription!: Subscription;
    private handleTabContentVisibility: Map<boolean, (index: number) => void> = new Map<boolean, (index: number) => void>();
    private timelineOpened: boolean = false;

    constructor(
        private actions: Actions,
        public seSide: SeSideService,
        private store: Store<RootState>,
    ) {
        this.handleTabContentVisibility.set(true, this.showTabContent);
        this.handleTabContentVisibility.set(false, this.hideTabContent);
        this.rightSlider$ = this.store.select((state) => selectSeButtonsToolbarRightSlider(state));
    }

    ngOnInit() {
        const rightSub: Subscription = this.rightSlider$.subscribe((options) => {
            this.show = options.enabled;
            this.closed = options.enabled && !options.checked;
            if (this.closed) {
                this.unselectTabs();
            }

            if (this.selectedTabIndex === -1 && !this.closed) {
                this.selectFirstTab();
            }
        });

        this.actionSubscription = this.actions.pipe(ofType(seWorklistType)).subscribe((formType) => {
            if (this.seSide.previousFormType !== formType.formType) {
                this.formType = formType.formType;
                this.timelineOpened = false;
            } else {
                this.timelineOpened = true;
            }
            this.formType = formType.formType;
            this.seSide.previousFormType = formType.formType;
        });

        this.timelineStateSubscription = this.store.pipe(select(selectSeButtonsTimeline)).subscribe((el) => {
            this.isTimelineActiveOnWl = el ? el.active : false;
        });

        const sideTabSub: Subscription = this.activeSideComponents$.subscribe((components) => {
            this.activeSideComponentsSnapshot = components;
        });

        this.subscriptions.push(rightSub);
        this.subscriptions.push(sideTabSub);
    }

    private selectFirstTab() {
        this.selectedTabIndex = 0;
        this.hideContent = false;
    }

    private unselectTabs() {
        this.selectedTabIndex = -1;
        this.hideContent = true;
    }

    ngAfterViewInit() {
        this.reportingContainer?.changes.subscribe((change: QueryList<ViewContainerRef>) => {
            if (change.first) {
                this.reportingComponentSubscription = this.listenForSideComponentChanges();
            } else {
                this.reportingComponentSubscription?.unsubscribe();
            }
        });
    }

    ngOnDestroy() {
        this.reportingContainer?.forEach((ref) => ref.clear());
        this.reportingComponentSubscription?.unsubscribe();
        this.actionSubscription.unsubscribe();
        this.timelineStateSubscription.unsubscribe();
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }

    private showTabContent = (index: number) => {
        this.selectedTabIndex = index;
    };

    private hideTabContent = (_: number) => {
        this.selectedTabIndex = -1;
    };

    onSelectedTabChange(event: MatTabChangeEvent) {
        const sideComponent = this.activeSideComponentsSnapshot[event.index];
        this.handleTabContentVisibility!.get(!this.hideContent)?.(event.index);
        this.dispatchClickedTab(sideComponent);
        setTimeout(() => {
            this.matTabs?.realignInkBar(); // Serve altrimenti al primo avvio l'animazione non parte
        }, 0);
    }

    private toggleOneNoteSlider() {
        this.store.dispatch(seToolbarToggleRightSlider());
        if (!this.timelineOpened && this.selectedTabIndex !== -1 && this.isTimelineActiveOnWl) {
            this.store.dispatch(seToolbarToggleTimeline());
            this.timelineOpened = true;
        }
    }

    private listenForSideComponentChanges() {
        return this.seSide.reportingComponent$.subscribe((props) => {
            this.reportingContainer?.forEach((ref) => ref.clear());

            if (props) {
                this.createReportingComponent(props);
            }
        });
    }

    private createReportingComponent(props: ReportingComponentProps<any>) {
        const component = this.reportingContainer?.first?.createComponent(props.component);
        for (const [key, value] of Object.entries(props.inputs)) {
            component!.instance[key] = value;
        }
    }

    private dispatchClickedTab(component: any) {
        if (!component || !component.type) return;
        this.store.dispatch(sideTabClicked({ componentType: component.type }));
    }

    onClick(tabIndex: number, component: any) {
        if (!!this.closed) {
            this.hideContent = false;
            this.toggleOneNoteSlider();
            this.selectedTabIndex = tabIndex;
            this.dispatchClickedTab(component);
            return;
        }

        if (this.selectedTabIndex === -1) {
            this.hideContent = false;
            this.toggleOneNoteSlider();
            this.selectedTabIndex = tabIndex;
            this.dispatchClickedTab(component);
            return;
        }

        if (this.selectedTabIndex === tabIndex) {
            this.hideContent = true;
            this.handleTabContentVisibility!.get(!this.hideContent).apply(tabIndex);
            this.dispatchClickedTab(component);
            this.toggleOneNoteSlider();
        }
    }
}
